import React from 'react'
import NextImage from 'next/image'
import Link from 'next/link'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'

import {
  useQueryAPIGetKSSelectionCandidate,
  useQueryAPIGetKSSelectionProposal,
} from 'api/bcks/useAPIBCKSInvitation'
import useKSPSTrackEvent from 'app/BCKSPage/hooks/useKSPSTrackEvent'

import type { ResponseSuccessKSSelectionCandidate } from 'api/bcks/useAPIBCKSInvitation'

interface InvitationStateMap {
  title: string
  buttonText?: string
  imageSrc: string
  targetUrl?: string
}

const getUIKSStateMap = (
  candidateData: ResponseSuccessKSSelectionCandidate,
  deadlineDate: string
): InvitationStateMap => {
  const {
    selectionCandidateState: state,
    selectionState,
    selectionType,
  } = candidateData ?? {}

  switch (state) {
    case 'invitation':
      return {
        title: 'Anda terpilih dan diundang untuk mengikuti seleksi!',
        buttonText: 'Lihat undangan',
        imageSrc: '/images/bcks/img-invitation-default.png',
        targetUrl: '/bcks/invitation',
      }
    case 'notInvited':
      return {
        title:
          'Seleksi sedang berlangsung. Anda dapat mengusulkan diri untuk mengikuti seleksi.',
        buttonText: 'Cek cara usulkan diri',
        imageSrc: '/images/bcks/img-invitation-default.png',
        targetUrl: '/bcks/self-proposal',
      }
    case 'documentSubmission':
      const shownDate = new Date(deadlineDate)
        .toLocaleString('id-ID', {
          timeZone: 'Asia/Jakarta',
          dateStyle: 'long',
          timeStyle: 'short',
        })
        .replace('.', ':') // Replace period in time, ex: 23.59 to 23:59
      return {
        title: `Kumpulkan berkas maksimal ${shownDate} WIB`,
        buttonText: 'Cek',
        imageSrc: '/images/bcks/img-invitation-default.png',
        targetUrl: '/bcks/invitation/precollection',
      }
    case 'documentVerification':
    case 'documentVerificationFailed':
      if (
        selectionType === 'administration' &&
        selectionState === 'documentVerification'
      ) {
        return {
          title:
            'Dalam proses pengecekan berkas. Mohon cek rincian tahapan secara berkala.',
          buttonText: 'Cek rincian tahapan',
          imageSrc: '/images/bcks/img-files-magnifying-glass-progress.png',
          targetUrl: '/bcks/invitation/final',
        }
      }
      break
    case 'adminFinished':
    case 'adminFailed':
    case 'adminCanceled':
      if (selectionType === 'administration') {
        if (selectionState === 'documentVerification') {
          return {
            title:
              'Dalam proses pengecekan berkas. Mohon cek rincian tahapan secara berkala.',
            buttonText: 'Cek rincian tahapan',
            imageSrc: '/images/bcks/img-files-magnifying-glass-progress.png',
            targetUrl: '/bcks/invitation/final',
          }
        }
        if (selectionState === 'finished') {
          return {
            title: 'Informasi tentang Seleksi Administrasi Anda sudah tersedia',
            buttonText: 'Cek',
            imageSrc: '/illustrations/img-collect-files-folder.png',
            targetUrl: '/bcks/invitation/final',
          }
        }
      }
      break
    case 'substanceInProgress':
      if (selectionType === 'substance' && selectionState === 'finished') {
        return {
          title: 'Anda lolos Seleksi Administrasi! Segera unggah foto peserta.',
          buttonText: 'Cek',
          imageSrc: '/illustrations/img-collect-files-folder.png',
          targetUrl: '/bcks/invitation/final',
        }
      }
      break
    case 'overrideFinished':
      if (selectionType === 'override' && selectionState === 'finished') {
        return {
          title:
            'Informasi Hasil Seleksi sudah tersedia! Hubungi Dinas untuk selengkapnya.',
          buttonText: '',
          imageSrc: '/illustrations/img-announcement-speaker.png',
          targetUrl: '',
        }
      }
      break
  }

  return undefined
}

export const KSSelectionCard = () => {
  const { KSPSTrackEvent } = useKSPSTrackEvent()

  // -------------------------------------------
  // Get selection candidate and its derivatives
  // -------------------------------------------
  const { data: candidateData } = useQueryAPIGetKSSelectionCandidate()
  const deadlineDate = candidateData?.deadlineDate
  const uiKSState = getUIKSStateMap(candidateData, deadlineDate)
  const hasCandidateSelection =
    (!!deadlineDate || candidateData?.selectionCandidateState === 'finished') &&
    uiKSState

  // ---------------------------------------------
  // Get KS selection proposal and its derivatives
  // ---------------------------------------------
  const { data: proposalData } = useQueryAPIGetKSSelectionProposal({
    staleTime: 60 * 60 * 1000, // Stale after 60min, force to refetch upon mounting
    useErrorBoundary: false, // Prevent whole page error
  })
  const hasCandidateToPropose = proposalData?.candidates?.length > 0

  // ------------------
  // Conditional render
  // ------------------
  const shouldShow = hasCandidateSelection || hasCandidateToPropose

  if (!shouldShow) {
    return null
  }

  return (
    <section className="px-4">
      <Text
        variant="heading-md"
        className="mb-2"
        as="h2"
        data-testid="ks-selection-card"
      >
        Seleksi Kepala Sekolah
      </Text>

      <div className="space-y-4">
        {hasCandidateSelection && (
          <div className="rounded shadow p-4 flex justify-between space-x-4 bg-white">
            <div className="flex flex-col items-start gap-5">
              <Text data-testid="ks-selection-card-title">
                {uiKSState.title}
              </Text>

              {uiKSState.buttonText && uiKSState.targetUrl && (
                <Link href={uiKSState.targetUrl} passHref>
                  <Button
                    as="a"
                    type={undefined}
                    className="min-w-36"
                    color="black"
                    size="sm"
                    data-testid="ks-selection-card-link"
                    onClick={() => {
                      KSPSTrackEvent({
                        event_name: 'click_ksps_banner',
                        additional_info: [
                          {
                            key: 'cta_banner',
                            value: uiKSState.buttonText,
                          },
                        ],
                      })
                    }}
                  >
                    {uiKSState.buttonText}
                  </Button>
                </Link>
              )}
            </div>
            <div className="flex flex-col justify-center shrink-0">
              <NextImage
                data-testid="ks-selection-card-image"
                src={uiKSState.imageSrc}
                width={60}
                height={60}
                alt="Perangkat Ajar"
              />
            </div>
          </div>
        )}

        {hasCandidateToPropose && (
          <div className="rounded shadow p-4 flex justify-between space-x-4 bg-white">
            <div className="flex flex-col items-start gap-3">
              <Text>
                Seleksi sedang berlangsung. Anda dapat mengusulkan Guru Anda
                untuk ikut seleksi.
              </Text>

              <Link href="/bcks/ks-proposal" passHref>
                <Button
                  as="a"
                  type={undefined}
                  color="black"
                  size="sm"
                  data-testid="ks-selection-card-link"
                  onClick={() => {
                    KSPSTrackEvent({
                      event_name: 'click_ksps_banner',
                      additional_info: [
                        {
                          key: 'cta_banner',
                          value: 'Usulkan guru',
                        },
                      ],
                    })
                  }}
                >
                  Usulkan guru
                </Button>
              </Link>
            </div>
            <div className="flex flex-col justify-center shrink-0">
              <NextImage
                src="/images/bcks/img-invitation-default.png"
                width={60}
                height={60}
                alt="Perangkat Ajar"
              />
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
